import { Heading, Rhythm, Stack } from '@trmediaab/zebra';
import type { PaymentMethod } from '@trmediaab/zebra-types/trhub';
import { useCallback, useMemo, useState } from 'react';

import Cart from '../../components/Cart';
import { RadioInput } from '../../components/Form';
import Layout from '../../components/Layout';
import ProgressBar from '../../components/ProgressBar';
import { useImmutableState } from '../../state';
import useUser from '../../state/useUser';
import type { OnAuthenticationHandler } from '../../types/OnAuthenticationHandler';
import { isShippingOrder } from '../../utils/orderUtils';
import { useAnalytics } from '../../utils/useAnalytics';
import AutogiroForm from './Autogiro';
import SwedbankPayForm from './SwedbankPay';
import type { PaymentFormProps } from './types';
import UserInfo from './UserInfo';

interface PaymentProps extends OnAuthenticationHandler {
  onNext: () => void;
  onPrev: () => void;
}

const PaymentView = ({ onNext, onPrev, onAuthentication }: PaymentProps) => {
  const { user } = useUser();
  const { site, order, hasProductOptions, disallowUserChange } =
    useImmutableState();

  if (order == null) {
    // Note: This should never happen, but we want to be sure
    throw new Error('Cannot render payment view without order');
  }

  const hasShipping = isShippingOrder(order);
  const analytics = useAnalytics();

  const showUserInfo = !hasShipping && (user == null || !disallowUserChange);

  const paymentMethods: {
    key: PaymentMethod;
    label: string;
    component: React.ComponentType<PaymentFormProps>;
  }[] = [
    {
      key: 'card',
      label: 'Kortbetalning',
      component: SwedbankPayForm,
    },
  ];

  if (order.autogiroPossible) {
    paymentMethods.push({
      key: 'autogiro',
      label: 'Autogiro',
      component: AutogiroForm,
    });
  }

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    order.paymentMethod,
  );

  const Form =
    paymentMethods.find(item => item.key === paymentMethod)?.component ?? null;

  const onPaymentComplete = useCallback(() => {
    analytics.purchase(order);
    onNext();
  }, [analytics, onNext, order]);

  const layoutAreas = useMemo(() => {
    if (hasShipping) {
      return {
        oneCol: [
          'products',
          hasProductOptions && 'changeProduct',
          'shipping',
          'payment',
        ].filter(Boolean),
        twoCol: [
          'products shipping',
          hasProductOptions && 'changeProduct .',
          'payment payment',
        ].filter(Boolean),
      };
    } else {
      return {
        oneCol: [
          'products',
          hasProductOptions && 'changeProduct',
          showUserInfo && 'user',
          'payment',
        ].filter(Boolean),
        twoCol: [
          showUserInfo ? 'products user' : 'products products',
          hasProductOptions &&
            (showUserInfo
              ? 'changeProduct user'
              : 'changeProduct changeProduct'),
          'payment payment',
        ].filter(Boolean),
      };
    }
  }, [hasProductOptions, hasShipping, showUserInfo]);

  return (
    <Layout areas={layoutAreas}>
      <ProgressBar progress={100} />

      <Cart
        order={order}
        editable={false}
        showChangeProduct={hasProductOptions === true}
      />

      {showUserInfo && (
        <Layout.area area="user">
          <UserInfo onPrev={onPrev} order={order} />
        </Layout.area>
      )}

      <Layout.area area="payment">
        <Rhythm between="6">
          {paymentMethods.length > 1 && (
            <Rhythm between="4">
              <Heading.h4 variant="checkout" textAlign="center">
                Välj betalsätt
              </Heading.h4>

              <Stack axis="horizontal" between="2,5">
                {paymentMethods.map(item => (
                  <RadioInput
                    key={item.key}
                    name="paymentMethod"
                    value={item.key}
                    checked={paymentMethod === item.key}
                    label={item.label}
                    containerStyles={{
                      display: 'inline-flex',
                      flex: 1,
                      border: 1,
                      borderColor:
                        paymentMethod === item.key
                          ? 'checkout.checkboxSelected'
                          : undefined,
                      borderRadius: 1,
                      bg:
                        paymentMethod === item.key
                          ? 'checkout.checkboxBgSelected'
                          : undefined,
                      p: [2, null, '2,5'],
                    }}
                    onChange={e => {
                      const { checked, value } = e.target;
                      if (checked) {
                        setPaymentMethod(value as PaymentMethod);
                      }
                    }}
                  />
                ))}
              </Stack>
            </Rhythm>
          )}
          {Form != null && (
            <Form
              order={order}
              site={site}
              user={user}
              onComplete={onPaymentComplete}
              onAuthentication={onAuthentication}
            />
          )}
        </Rhythm>
      </Layout.area>
    </Layout>
  );
};

export default PaymentView;
